import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, Download, RefreshCw, Building2, Filter,
  DollarSign, ShoppingBag, FileText, AlertTriangle, ChevronDown, ChevronUp
} from 'lucide-react';
import { API_URL } from '../../services/api';


function fmt(n) {
  if (!n && n !== 0) return '—';
  return parseFloat(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' DH';
}
function fmtN(n) {
  if (!n && n !== 0) return '—';
  return parseFloat(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const MOIS = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];

export default function RapportFinancier() {
  const now = new Date();
  const [periode, setPeriode]         = useState('mois');
  const [annee, setAnnee]             = useState(now.getFullYear());
  const [mois, setMois]               = useState(now.getMonth() + 1);
  const [boutiqueId, setBoutiqueId]   = useState('');
  const [boutiques, setBoutiques]     = useState([]);
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(false);
  const [showInstance, setShowInstance] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/boutiques`).then(r => r.json()).then(d => setBoutiques(Array.isArray(d) ? d : [])).catch(err => console.error('Erreur:', err));
    load();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ periode, annee, mois });
      if (boutiqueId) params.append('boutique_id', boutiqueId);
      const r = await fetch(`${API_URL}/rapports/financier?${params}`);
      const d = await r.json();
      setData(d);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [periode, annee, mois, boutiqueId]);

  useEffect(() => { load(); }, [periode, annee, mois, boutiqueId]);

  const v = data?.ventes || {};
  const a = data?.achats || {};
  const caTotal     = parseFloat(v.ca_total || 0);
  const caOfficiel  = parseFloat(v.ca_officiel || 0);
  const caInstance  = parseFloat(v.ca_instance || 0);
  const totalAchats = parseFloat(a.total_achats || 0);
  const margeOff    = caOfficiel - totalAchats;
  const encaisse       = parseFloat(data?.encaissements?.total || 0);
  const creditsEnCours = parseFloat(data?.credits_en_cours || 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Rapport Financier</h1>
          <p className="text-sm text-gray-500 mt-0.5">Vue d'ensemble des ventes et dépenses</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Periode */}
          <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden text-sm">
            {[{v:'mois',l:'Mois'},{v:'annee',l:'Année'},{v:'tout',l:'Tout'}].map(o => (
              <button key={o.v} onClick={() => setPeriode(o.v)}
                className={`px-3 py-2 transition-all ${periode===o.v ? 'bg-gray-800 text-white font-semibold' : 'text-gray-500 hover:bg-gray-50'}`}
              >{o.l}</button>
            ))}
          </div>
          {/* Année */}
          <select value={annee} onChange={e => setAnnee(+e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white outline-none cursor-pointer">
            {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {/* Mois */}
          {periode === 'mois' && (
            <select value={mois} onChange={e => setMois(+e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white outline-none cursor-pointer">
              {MOIS.map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
          )}
          {/* Boutique */}
          <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-xl bg-white text-sm">
            <Building2 className="h-4 w-4 text-gray-400" />
            <select value={boutiqueId} onChange={e => setBoutiqueId(e.target.value)}
              className="outline-none bg-transparent cursor-pointer">
              <option value="">Toutes boutiques</option>
              {boutiques.map(b => <option key={b.id} value={b.id}>{b.nom}</option>)}
            </select>
          </div>
          <button onClick={load} className="p-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
            <RefreshCw className={`h-4 w-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading && !data && (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="h-8 w-8 animate-spin text-gray-300" />
        </div>
      )}

      {data && (
        <>
          {/* ── Cartes résumé ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-2">CA Total</div>
              <div className="text-3xl font-bold text-gray-900">{fmtN(caTotal)}</div>
              <div className="text-xs text-gray-400 mt-1">DH · {v.nb_total || 0} ventes</div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 p-5">
              <div className="text-xs text-blue-600 uppercase tracking-wide font-semibold mb-2 flex items-center gap-1">
                <FileText className="h-3 w-3" /> CA Officiel (déclaré)
              </div>
              <div className="text-3xl font-bold text-blue-800">{fmtN(caOfficiel)}</div>
              <div className="text-xs text-blue-500 mt-1">DH · {v.nb_officiel || 0} ventes</div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl border border-orange-100 p-5">
              <div className="text-xs text-orange-600 uppercase tracking-wide font-semibold mb-2 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Ventes en instance
              </div>
              <div className="text-3xl font-bold text-orange-700">{fmtN(caInstance)}</div>
              <div className="text-xs text-orange-500 mt-1">DH · {v.nb_instance || 0} ventes</div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-100 p-5">
              <div className="text-xs text-green-600 uppercase tracking-wide font-semibold mb-2">Marge nette déclarée</div>
              <div className={`text-3xl font-bold ${margeOff >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                {fmtN(margeOff)}
              </div>
              <div className="text-xs text-green-500 mt-1">DH (après achats)</div>
            </div>
          </div>

          {/* ── CA vs ENCAISSÉ (D1 11/06/2026) : l'écart = crédits accordés ── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h3 className="font-bold text-gray-700 mb-4">💵 Encaissé réel vs CA</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-cyan-50 to-sky-50 rounded-xl border border-cyan-100 p-4">
                <div className="text-xs text-cyan-600 uppercase tracking-wide font-semibold mb-1">Encaissé (période)</div>
                <div className="text-2xl font-bold text-cyan-800">{fmtN(encaisse)}</div>
                <div className="text-xs text-cyan-500 mt-1">
                  DH · {Object.entries(data.encaissements?.par_mode || {})
                    .map(([m, t]) => `${m} ${fmtN(t)}`).join(' · ') || 'aucun paiement enregistré'}
                </div>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl border border-gray-200 p-4">
                <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Écart CA − encaissé (période)</div>
                <div className={`text-2xl font-bold ${(caTotal - encaisse) > 0.01 ? 'text-amber-700' : 'text-gray-900'}`}>
                  {fmtN(caTotal - encaisse)}
                </div>
                <div className="text-xs text-gray-400 mt-1">DH · ventes non (encore) encaissées</div>
              </div>
              <div className="bg-gradient-to-br from-rose-50 to-red-50 rounded-xl border border-rose-100 p-4">
                <div className="text-xs text-rose-600 uppercase tracking-wide font-semibold mb-1">Crédits en cours (global)</div>
                <div className="text-2xl font-bold text-rose-700">{fmtN(creditsEnCours)}</div>
                <div className="text-xs text-rose-500 mt-1">DH dus par les clients (toutes périodes)</div>
              </div>
            </div>
          </div>

          {/* ── Statut fiscal (calculé via documents réels : factures / bons de livraison) ── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-700 flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-400" /> Déclaré / Noir / Total
              </h3>
              {(v.nb_a_arbitrer || 0) > 0 && (
                <button onClick={() => window.location.assign('/ventes-arbitrage')}
                        className="text-xs bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full font-semibold hover:bg-amber-200 transition-colors cursor-pointer"
                        title="Ouvrir la page d'arbitrage">
                  ⚠ {v.nb_a_arbitrer} vente{v.nb_a_arbitrer > 1 ? 's' : ''} à arbitrer ({fmtN(v.ca_a_arbitrer)} DH) →
                </button>
              )}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-100 p-4">
                <div className="text-xs text-emerald-600 uppercase tracking-wide font-semibold mb-1">Déclaré</div>
                <div className="text-2xl font-bold text-emerald-700">{fmtN(v.ca_declare)}</div>
                <div className="text-xs text-emerald-500 mt-1">DH · {v.nb_declare || 0} ventes (facture ou BL converti)</div>
              </div>
              <div className="bg-gradient-to-br from-stone-100 to-stone-200 rounded-xl border border-stone-300 p-4">
                <div className="text-xs text-stone-600 uppercase tracking-wide font-semibold mb-1">Noir</div>
                <div className="text-2xl font-bold text-stone-800">{fmtN(v.ca_noir)}</div>
                <div className="text-xs text-stone-500 mt-1">DH · {v.nb_noir || 0} ventes (BL sans facture)</div>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl border border-gray-200 p-4">
                <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Total</div>
                <div className="text-2xl font-bold text-gray-900">{fmtN(caTotal)}</div>
                <div className="text-xs text-gray-400 mt-1">DH · {v.nb_total || 0} ventes (hors champ exclu)</div>
              </div>
            </div>
            {(v.nb_hors_champ || 0) > 0 && (
              <div className="mt-3 text-xs text-gray-500">
                dont hors champ (perso/famille, exclu du CA) : <span className="font-semibold">{v.nb_hors_champ} vente{v.nb_hors_champ > 1 ? 's' : ''} · {fmtN(v.ca_hors_champ)} DH</span>
              </div>
            )}
          </div>

          {/* ── Par mode de paiement ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-gray-700 mb-4 flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-gray-400" /> Encaissements par mode
              </h3>
              {[
                { label: 'Espèces',  val: v.ca_especes,  color: 'bg-green-500' },
                { label: 'Carte',    val: v.ca_carte,    color: 'bg-blue-500' },
                { label: 'Chèque',   val: v.ca_cheque,   color: 'bg-amber-500' },
                { label: 'Virement', val: v.ca_virement,  color: 'bg-indigo-500' },
                { label: 'Or',       val: v.ca_or,        color: 'bg-yellow-500' },
                { label: 'Compte',   val: v.ca_compte,    color: 'bg-purple-500' },
              ].filter(r => parseFloat(r.val || 0) > 0).map(r => (
                <div key={r.label} className="flex items-center gap-3 mb-3">
                  <div className={`w-3 h-3 rounded-full ${r.color} shrink-0`} />
                  <div className="flex-1 text-sm text-gray-600">{r.label}</div>
                  <div className="font-bold text-gray-800">{fmt(r.val)}</div>
                  <div className="text-xs text-gray-400 w-10 text-right">
                    {caTotal > 0 ? Math.round(parseFloat(r.val||0)/caTotal*100) : 0}%
                  </div>
                </div>
              ))}
            </div>

            {/* Achats */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-gray-700 mb-4 flex items-center gap-2">
                <ShoppingBag className="h-4 w-4 text-gray-400" /> Achats matières
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Total achats</span>
                  <span className="font-bold text-gray-800">{fmt(a.total_achats)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    <FileText className="h-3.5 w-3.5 text-blue-400" /> Avec facture
                  </span>
                  <span className="font-bold text-blue-700">{fmt(a.achats_avec_facture)}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5 text-orange-400" /> Sans facture
                  </span>
                  <span className="font-bold text-orange-700">{fmt(a.achats_sans_facture)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Par boutique ── */}
          {data.ventes_par_boutique?.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-gray-700 mb-4">Ventes par boutique</h3>
              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {data.ventes_par_boutique.map((b, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-bold text-gray-800">{b.boutique || '—'}</div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">{b.nb} ventes</span>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-xs mt-2">
                      <div>
                        <div className="text-gray-400 uppercase tracking-wide mb-0.5">CA Total</div>
                        <div className="font-bold text-gray-800">{fmt(b.ca)}</div>
                      </div>
                      <div>
                        <div className="text-emerald-500 uppercase tracking-wide mb-0.5">Déclaré</div>
                        <div className="font-bold text-emerald-700">{fmt(b.ca_declare ?? b.ca_officiel)}</div>
                      </div>
                      <div>
                        <div className="text-stone-500 uppercase tracking-wide mb-0.5">Noir</div>
                        <div className="font-bold text-stone-700">{fmt(b.ca_noir)}</div>
                      </div>
                      <div>
                        <div className="text-orange-500 uppercase tracking-wide mb-0.5">Instance</div>
                        <div className="font-bold text-orange-600">{fmt(b.ca_instance)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-xs text-gray-400 uppercase tracking-wide">
                      <th className="text-left pb-3 font-semibold">Boutique</th>
                      <th className="text-right pb-3 font-semibold">Nb ventes</th>
                      <th className="text-right pb-3 font-semibold">CA Total</th>
                      <th className="text-right pb-3 font-semibold text-emerald-600">Déclaré</th>
                      <th className="text-right pb-3 font-semibold text-stone-600">Noir</th>
                      <th className="text-right pb-3 font-semibold text-orange-500">Instance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ventes_par_boutique.map((b, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-3 font-medium text-gray-800">{b.boutique || '—'}</td>
                        <td className="py-3 text-right text-gray-600">{b.nb}</td>
                        <td className="py-3 text-right font-bold text-gray-800">{fmt(b.ca)}</td>
                        <td className="py-3 text-right text-emerald-700">{fmt(b.ca_declare ?? b.ca_officiel)}</td>
                        <td className="py-3 text-right text-stone-700">{fmt(b.ca_noir)}</td>
                        <td className="py-3 text-right text-orange-600">{fmt(b.ca_instance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Ventes en instance — détail (collapsible) ── */}
          {caInstance > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-2xl overflow-hidden">
              <button
                onClick={() => setShowInstance(!showInstance)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-orange-100 transition-colors"
              >
                <div className="flex items-center gap-2 font-bold text-orange-800">
                  <AlertTriangle className="h-4 w-4" />
                  Ventes en instance — {v.nb_instance} transaction(s) · {fmt(caInstance)}
                </div>
                {showInstance ? <ChevronUp className="h-4 w-4 text-orange-600" /> : <ChevronDown className="h-4 w-4 text-orange-600" />}
              </button>
              {showInstance && (
                <div className="px-5 pb-5">
                  <p className="text-xs text-orange-600 mb-3">Ces ventes ne sont pas déclarées. Elles n'apparaissent pas dans le CA officiel.</p>
                  {/* Mobile cards */}
                  <div className="md:hidden space-y-3">
                    {(data.ventes_recentes || []).filter(r => r.type_caisse === 'instance').map((r, i) => (
                      <div key={i} className="bg-white rounded-lg p-3 border border-orange-200">
                        <div className="flex justify-between items-start mb-1">
                          <div className="font-mono text-xs font-bold text-gray-800">{r.reference_article || r.bijou_nom || r.commentaire?.slice(0,30) || '—'}</div>
                          <div className="font-bold text-orange-700">{fmt(r.prix_vente)}</div>
                        </div>
                        <div className="text-xs text-gray-500">{r.client_nom || '—'} · {r.boutique_nom || '—'}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{new Date(r.date_vente).toLocaleDateString('fr-FR')}</div>
                      </div>
                    ))}
                  </div>
                  {/* Desktop table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-orange-700 uppercase tracking-wide border-b border-orange-200">
                          <th className="text-left pb-2">Date</th>
                          <th className="text-left pb-2">Article</th>
                          <th className="text-left pb-2">Client</th>
                          <th className="text-left pb-2">Boutique</th>
                          <th className="text-right pb-2">Montant</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.ventes_recentes || []).filter(r => r.type_caisse === 'instance').map((r,i) => (
                          <tr key={i} className="border-b border-orange-100">
                            <td className="py-2 text-gray-600">{new Date(r.date_vente).toLocaleDateString('fr-FR')}</td>
                            <td className="py-2 font-mono text-xs">{r.reference_article || r.bijou_nom || r.commentaire?.slice(0,30) || '—'}</td>
                            <td className="py-2 text-gray-600">{r.client_nom || '—'}</td>
                            <td className="py-2 text-gray-600">{r.boutique_nom || '—'}</td>
                            <td className="py-2 text-right font-bold text-orange-700">{fmt(r.prix_vente)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Top articles ── */}
          {data.top_articles?.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-gray-700 mb-4 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-purple-500" /> Top articles vendus
              </h3>
              <div className="space-y-2">
                {data.top_articles.slice(0,10).map((a,i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                    <div className="w-7 h-7 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold shrink-0">
                      {i+1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-800 truncate">{a.bijou_nom || a.reference_article}</div>
                      <div className="text-xs text-gray-400">{a.reference_article} · {a.categorie}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-bold text-gray-800">{fmt(a.ca)}</div>
                      <div className="text-xs text-gray-400">{a.nb_vendus} vendu(s)</div>
                    </div>
                    {a.type_caisse === 'instance' && (
                      <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full shrink-0">instance</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Récapitulatif déclaration fiscale ── */}
          <div className="bg-gradient-to-br from-blue-900 to-indigo-900 text-white rounded-2xl p-6">
            <h3 className="font-bold text-lg mb-5 flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-300" /> Récapitulatif — Déclaration fiscale
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white/10 rounded-xl p-4">
                <div className="text-blue-200 text-xs uppercase tracking-wide mb-1">Chiffre d'affaires déclaré</div>
                <div className="text-2xl font-bold">{fmtN(caOfficiel)} DH</div>
                <div className="text-blue-300 text-xs mt-1">{v.nb_officiel || 0} transactions</div>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <div className="text-blue-200 text-xs uppercase tracking-wide mb-1">Achats avec facture</div>
                <div className="text-2xl font-bold">{fmtN(a.achats_avec_facture || 0)} DH</div>
                <div className="text-blue-300 text-xs mt-1">Charges déductibles</div>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <div className="text-blue-200 text-xs uppercase tracking-wide mb-1">Marge déclarée</div>
                <div className={`text-2xl font-bold ${margeOff >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                  {fmtN(margeOff)} DH
                </div>
                <div className="text-blue-300 text-xs mt-1">CA officiel − achats facturés</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
